import axios from "axios";
import { ctxHeaders } from "./phase1Api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api/assessments`;

const http = axios.create({ baseURL: API_BASE });

export const assessmentApi = {
  // Industries
  listIndustries: async (session) => {
    const { data } = await http.get("/industries", { headers: ctxHeaders(session) });
    return data;
  },
  addIndustry: async (payload, session) => {
    const { data } = await http.post("/industries", payload, { headers: ctxHeaders(session) });
    return data;
  },

  // Templates
  listTemplates: async (params, session) => {
    const qs = new URLSearchParams();
    if (params?.industry) qs.set("industry", params.industry);
    if (params?.type) qs.set("type", params.type);
    if (params?.status) qs.set("status", params.status);
    const { data } = await http.get(`/templates?${qs}`, { headers: ctxHeaders(session) });
    return data;
  },
  getTemplate: async (id, session) => {
    const { data } = await http.get(`/templates/${id}`, { headers: ctxHeaders(session) });
    return data;
  },
  createTemplate: async (payload, session) => {
    const { data } = await http.post("/templates", payload, { headers: ctxHeaders(session) });
    return data;
  },
  updateTemplate: async (id, payload, session) => {
    const { data } = await http.patch(`/templates/${id}`, payload, { headers: ctxHeaders(session) });
    return data;
  },
  deleteTemplate: async (id, session) => {
    const { data } = await http.delete(`/templates/${id}`, { headers: ctxHeaders(session) });
    return data;
  },
  duplicateTemplate: async (id, payload, session) => {
    const { data } = await http.post(`/templates/${id}/duplicate`, payload, { headers: ctxHeaders(session) });
    return data;
  },
  updatePillarWeights: async (id, weights, session) => {
    const { data } = await http.patch(`/templates/${id}/pillar-weights`, { pillar_weights: weights }, { headers: ctxHeaders(session) });
    return data;
  },

  // Questions
  listQuestions: async (templateId, session) => {
    const { data } = await http.get(`/templates/${templateId}/questions`, { headers: ctxHeaders(session) });
    return data;
  },
  addQuestion: async (templateId, payload, session) => {
    const { data } = await http.post(`/templates/${templateId}/questions`, payload, { headers: ctxHeaders(session) });
    return data;
  },
  updateQuestion: async (templateId, questionId, payload, session) => {
    const { data } = await http.put(`/templates/${templateId}/questions/${questionId}`, payload, { headers: ctxHeaders(session) });
    return data;
  },
  deleteQuestion: async (templateId, questionId, session) => {
    const { data } = await http.delete(`/templates/${templateId}/questions/${questionId}`, { headers: ctxHeaders(session) });
    return data;
  },
  reorderQuestions: async (templateId, order, session) => {
    const { data } = await http.post(`/templates/${templateId}/questions/reorder`, { order }, { headers: ctxHeaders(session) });
    return data;
  },
  bulkImport: async (templateId, csv, session) => {
    const { data } = await http.post(`/templates/${templateId}/questions/bulk-import`, { template_id: templateId, questions_csv: csv }, { headers: ctxHeaders(session) });
    return data;
  },
  checkCoverage: async (templateId, session) => {
    const { data } = await http.get(`/templates/${templateId}/coverage`, { headers: ctxHeaders(session) });
    return data;
  },
  generateAI: async (templateId, payload, session) => {
    const { data } = await http.post(`/templates/${templateId}/generate-ai`, payload, { headers: ctxHeaders(session), timeout: 180000 });
    return data;
  },

  // Sessions
  createSession: async (payload, session) => {
    const { data } = await http.post("/sessions", payload, { headers: ctxHeaders(session) });
    return data;
  },
  listSessions: async (params, session) => {
    const qs = new URLSearchParams();
    if (params?.prospect_id) qs.set("prospect_id", params.prospect_id);
    const { data } = await http.get(`/sessions?${qs}`, { headers: ctxHeaders(session) });
    return data;
  },
  getSession: async (id, session) => {
    const { data } = await http.get(`/sessions/${id}`, { headers: ctxHeaders(session) });
    return data;
  },
  saveResponse: async (sessionId, payload, session) => {
    const { data } = await http.post(`/sessions/${sessionId}/responses`, payload, { headers: ctxHeaders(session) });
    return data;
  },
  completeSession: async (sessionId, session) => {
    const { data } = await http.post(`/sessions/${sessionId}/complete`, null, { headers: ctxHeaders(session) });
    return data;
  },
  // Admin-only demo: apply a batch of responses then complete the session.
  demoAutoComplete: async (sessionId, responses, profileKey, session) => {
    const { data } = await http.post(
      `/sessions/${sessionId}/demo-auto-complete`,
      { responses, profile_key: profileKey },
      { headers: ctxHeaders(session), timeout: 180000 },
    );
    return data;
  },

  // Results
  generateShortlist: async (resultId, session) => {
    const { data } = await http.post(`/results/${resultId}/generate-shortlist`, null, { headers: ctxHeaders(session), timeout: 180000 });
    return data;
  },
  generatePlan: async (resultId, session) => {
    const { data } = await http.post(`/results/${resultId}/generate-plan`, null, { headers: ctxHeaders(session), timeout: 180000 });
    return data;
  },

  // Talking Points
  generateTalkingPoints: async ({ session_id }, session) => {
    const { data } = await http.post(`/sessions/${session_id}/generate-talking-points`, null, {
      headers: ctxHeaders(session), timeout: 180000,
    });
    return data;
  },
  regenerateSingleTalkingPoint: async ({ question_id }, session) => {
    const { data } = await http.post(`/questions/${question_id}/regenerate-talking-point`, null, {
      headers: ctxHeaders(session), timeout: 60000,
    });
    return data;
  },

  // Consultant Comments
  saveConsultantComment: async (sessionId, questionId, comment, session) => {
    const { data } = await http.patch(
      `/sessions/${sessionId}/responses/${questionId}/comment`,
      { consultant_comments: comment },
      { headers: ctxHeaders(session) },
    );
    return data;
  },
};
